// Utility functions for the dashboard
import { formatPrice as formatPriceUtil, parsePrice as parsePriceUtil } from './price-utils';

/**
 * Format a date/time for display
 */
export function formatDateTime(dateStr: string | Date): string {
  const date = new Date(dateStr);
  return date.toLocaleString('de-AT', { timeZone: 'Europe/Vienna' });
}

/**
 * Format a date only
 */
export function formatDate(dateStr: string | Date): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-AT', { timeZone: 'Europe/Vienna' });
}

/**
 * Format time only
 */
export function formatTime(dateStr: string | Date): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('de-AT', { timeZone: 'Europe/Vienna' });
}

/**
 * Parse price string to number (wrapper for centralized utility)
 */
export function parsePrice(price: string | number | null | undefined): number {
  return parsePriceUtil(price);
}

/**
 * Format price in EUR (wrapper for centralized utility)
 */
export function formatPrice(value: number | string | null | undefined): string {
  return formatPriceUtil(value, 'EUR');
}

/**
 * Format duration in seconds to human readable
 */
export function formatDuration(seconds: number): string {
  if (seconds > 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
  if (seconds > 60) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  }
  return `${seconds}s`;
}

/**
 * Format time since a date (e.g., "5m ago")
 */
export function formatTimeSince(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

/**
 * Parse European date format (DD.MM.YYYY) to ISO format
 */
export function parseEuroDate(dateStr: string): string | null {
  if (!dateStr) return null;
  
  // Check if already in ISO format
  if (dateStr.includes('-')) {
    return dateStr;
  }
  
  // Parse DD.MM.YYYY format
  const parts = dateStr.split('.');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return null;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Format number with locale
 */
export function formatNumber(value: number): string {
  return value.toLocaleString('de-AT');
}

/**
 * Get DOM element with type safety
 */
export function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/**
 * Set text content of element by ID
 */
export function setText(id: string, text: string | number): void {
  const el = $(id);
  if (el) el.textContent = String(text);
}

/**
 * Set innerHTML of element by ID
 */
export function setHTML(id: string, html: string): void {
  const el = $(id);
  if (el) el.innerHTML = html;
}

/**
 * Show element by ID
 */
export function show(id: string): void {
  const el = $(id);
  if (el) el.style.display = 'block';
}

/**
 * Hide element by ID
 */
export function hide(id: string): void {
  const el = $(id);
  if (el) el.style.display = 'none';
}

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Generate CSV content from data
 */
export function generateCSV(headers: string[], rows: (string | number)[][]): string {
  const csvRows = [headers, ...rows].map(row =>
    row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  );
  return csvRows.join('\n');
}

/**
 * Download content as file
 */
export function downloadFile(content: string, filename: string, mimeType: string = 'text/csv'): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
